'use client';
import { useQuery } from '@tanstack/react-query';

import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import NextImage from 'next/image';

import Footer from '../../../../components/Footer';
import ResourceItem from '../../../../components/ResourceItem';
import Wrapper from '../../../../components/Wrapper';
import buildRouteUrl from '../../../../helpers/buildRouteUrl';
import useIntl from '../../../../hooks/useIntl';
import { Link } from '../../../../i18n/navigation';
import api from '../../../../services/api';
import { Movie, PersonProfile, Tv } from '../../../../types/types';
import buildTitleUrl from '../../../../helpers/buildTitleUrl';

const CarouselSection = dynamic(
  () => import('../../../../components/CarouselSection'),
  {
    ssr: true
  }
);

interface PropTypes {
  initialData: {
    person: PersonProfile;
    personMovies: Movie[];
    personTvs: Tv[];
  };
  isMobile?: boolean;
}

async function getPerson(
  personId: string,
  headers?: any
): Promise<{ data: PersonProfile }> {
  const response = await api.get(`/v1/persons/${personId}`, {
    headers
  });

  return response.data;
}

async function getPersonMovies(
  personId: string,
  params?: any,
  headers?: any
): Promise<{ data: Movie[] }> {
  const response = await api.get(
    `/v1/persons/${personId}/movies?offset=0&limit=20`,
    {
      params,
      headers
    }
  );

  return response.data;
}

async function getPersonTvs(
  personId: string,
  params?: any,
  headers?: any
): Promise<{ data: Tv[] }> {
  const response = await api.get(
    `/v1/persons/${personId}/tv?offset=0&limit=20`,
    {
      params,
      headers
    }
  );

  return response.data;
}

function ClientPerson({ initialData, isMobile }: PropTypes) {
  const intl = useIntl();

  const t = useTranslations();

  const personId = initialData.person._id as string;

  const personQuery = useQuery<{ data: PersonProfile }>({
    queryKey: [`person-${personId}`],
    queryFn: async () => {
      return await getPerson(personId, {
        'accept-language': intl.full_language
      });
    },
    initialData: { data: initialData.person }
  });

  const personMoviesQuery = useQuery<{
    data: Movie[];
  }>({
    queryKey: [`person-${personId}-movies`],
    queryFn: async () => {
      return await getPersonMovies(
        personId,
        {
          region: intl.locale
        },
        {
          'accept-language': intl.full_language
        }
      );
    },
    initialData:  { data: initialData.personMovies }
  })

  const personTvsQuery = useQuery<{
    data: Tv[];
  }>({
    queryKey: [`person-${personId}-tv`],
    queryFn: async () => {
      return await getPersonTvs(
        personId,
        {
          region: intl.locale
        },
        {
          'accept-language': intl.full_language
        }
      );
    },
    initialData: { data: initialData.personTvs }
  });

  return (
    <Wrapper>
      <div className="m-h-[100vh]">
        {personQuery.isSuccess && (
          <div className="flex flex-col w-full">
            <div>
              <div
                className={`
                flex
                flex-col
                md:flex-row
                px-4 md:px-10
                py-4 md:py-6
                relative
              `}
              >
                {isMobile && (
                  <div className="flex flex-col md:hidden">
                    <NextImage
                      className="rounded-md bg-gray-400 mr-4 z-[2] w-[70px] h-[105px]"
                      src={`https://cdn-proxy.trakio.app/t/p/w500${personQuery.data?.data?.profile_path}`}
                      alt={t(`images_alts.person_image`, {
                        name: personQuery.data.data.name
                      })}
                      width={70}
                      height={105}
                      unoptimized
                    />

                    <h1 className="text-white text-2xl font-bold">
                      {personQuery.data?.data.name}
                    </h1>
                  </div>
                )}
                {typeof isMobile === 'undefined' && (
                  <div className="flex md:hidden">
                    <NextImage
                      className="rounded-md bg-gray-400 mr-4 z-[2] w-[70px] h-[105px]"
                      width={70}
                      height={105}
                      src={`https://cdn-proxy.trakio.app/t/p/w500${personQuery.data?.data?.profile_path}`}
                      alt={t(`images_alts.person_image`, {
                        name: personQuery.data.data.name
                      })}
                      unoptimized
                    />

                    <h1 className="text-white text-2xl font-bold">
                      {personQuery.data?.data.name}
                    </h1>
                  </div>
                )}
                {!isMobile && (
                  <NextImage
                    className="rounded-md bg-gray-400 mr-4 z-[2] hidden md:block w-[360px] h-[540px]"
                    width={360}
                    height={540}
                    src={`https://cdn-proxy.trakio.app/t/p/w500${personQuery.data?.data?.profile_path}`}
                    alt={t(`images_alts.person_image`, {
                      name: personQuery.data.data.name
                    })}
                    unoptimized
                  />
                )}
                <div className="flex flex-col w-full">
                  {!isMobile && (
                    <h1 className="hidden md:block text-white text-3xl font-bold">
                      {personQuery.data?.data.name}
                    </h1>
                  )}
                  <p className="w-[100%] lg:w-[60%] text-white text-md mt-1 line-clamp-[20] md:line-clamp-12">
                    {personQuery.data?.data.biography}
                  </p>
                </div>
              </div>
            </div>

            <div
              className="flex flex-row items-start justify-between flex-wrap px-4 md:px-10
                py-4 md:py-6"
            >
              <div className="w-full">
                {(personMoviesQuery.data?.data.length || 0) > 0 && (
                  <CarouselSection
                    id={`persons-${personId}-movies`}
                    title={t('person.movies')}
                    type="resource"
                    totalItems={personMoviesQuery.data?.data.length || 0}
                    className="mb-6"
                    loadMore
                    loadMoreHref={buildRouteUrl(`/person/${personId}/movies/`)}
                  >
                    {personMoviesQuery.data?.data.map(item => {
                      return (
                        <Link
                          key={item._id}
                          href={buildTitleUrl({
                            type: 'movie',
                            locale: intl.locale.toLowerCase(),
                            shortId: item.short_id,
                            slugs: item.slugs,
                          })}
                        >
                          <div className="embla__slide">
                            <ResourceItem
                              className="mr-2"
                              data={{ ...item, type: 'movie' }}
                            />
                          </div>
                        </Link>
                      );
                    })}
                  </CarouselSection>
                )}

                {(personTvsQuery.data?.data.length || 0) > 0 && (
                  <CarouselSection
                    id={`persons-${personId}-tvs`}
                    title={t('person.tv_shows')}
                    type="resource"
                    totalItems={personTvsQuery.data?.data.length || 0}
                    className="mb-6"
                    loadMore
                    loadMoreHref={buildRouteUrl(
                      `/person/${personId}/tv-shows/`
                    )}
                  >
                    {personTvsQuery.data?.data.map(item => {
                      return (
                        <Link
                          key={item._id}
                          href={buildTitleUrl({
                            type: 'tv',
                            locale: intl.locale.toLowerCase(),
                            shortId: item.short_id,
                            slugs: item.slugs,
                          })}
                        >
                          <div className="embla__slide">
                            <ResourceItem
                              className="mr-2"
                              data={{ ...item, type: 'tv' }}
                            />
                          </div>
                        </Link>
                      );
                    })}
                  </CarouselSection>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </Wrapper>
  );
}

export default ClientPerson;
